import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCalendarStatus } from './api';

function findActiveMeeting(events, now = new Date()) {
  if (!Array.isArray(events)) return null;
  return (
    events.find((event) => {
      if (!event || event.is_all_day) return false;
      if (String(event.user_response_status || '').toLowerCase() === 'declined') {
        return false;
      }
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
      return start <= now && now < end;
    }) || null
  );
}

/**
 * Poll today's Outlook events and derive whether the user is currently in a meeting.
 * Disabled when calendar is not connected.
 */
export function useActiveMeeting({ enabled = true, refetchIntervalMs = 30000 } = {}) {
  const { data: calendarStatus, isLoading: statusLoading } = useCalendarStatus();
  // /api/calendar/status returns { success, data: { connected, ... } }
  const calendarConnected = Boolean(
    calendarStatus?.data?.connected ?? calendarStatus?.connected
  );

  const queryEnabled = Boolean(enabled && calendarConnected);

  const { data: events = [], isLoading: eventsLoading, refetch } = useQuery({
    queryKey: ['calendarTodayForMeetingPause'],
    queryFn: async () => {
      const response = await axios.get('/api/calendar/today');
      return response.data?.events ?? response.data ?? [];
    },
    enabled: queryEnabled,
    refetchInterval: queryEnabled ? refetchIntervalMs : false,
    staleTime: 15 * 1000,
    retry: false,
  });

  const activeMeeting = useMemo(() => findActiveMeeting(events), [events]);

  return {
    calendarConnected,
    activeMeeting,
    isMeetingActive: Boolean(activeMeeting),
    loading: statusLoading || (queryEnabled && eventsLoading),
    refetch,
  };
}

export default useActiveMeeting;
